// Suggests which nearby text label names an element: the label that reads as
// the tag the VLM saw on it (Symbol tool: metadata.vlm.tag_text; drawing
// processing: metadata.claude_tag). Matching mirrors the pipeline's
// (cv-processor/pinder_cv/claude_pipeline.py match_names): case and
// punctuation ignored, OCR's look-alike misreads tolerated ("RAOO1" for
// RA001), one character off allowed for longer tags, and a label may match
// one whole part of a split tag ("TI T0029" → "T0029"). Failing those, a
// close match: the share of characters the two have in common in order
// (longest common subsequence, as in Python's difflib), so a tag the VLM
// misread or clipped still finds its label ("5017A V-228" ↔ "F3017A GV-228").
//
// Text that repeats across the drawing ("GV-228" fifty times beside valves,
// "N.O.") is a spec or description, not a name: names are unique. A tag may
// legitimately appear twice (beside its pump, and where a line heads off to
// it), so only text seen REPEATED_TEXT_MIN times or more is ruled out — except
// for nozzles, whose names (N1, N2…) repeat on every vessel by design. That
// same repeated text is what suggestDescriptionLabels offers as descriptions.
//
// Only a suggestion: nothing here changes the element. The side pane shows
// it and creates the 'names' link only once the user accepts and saves.
import { rankTextLabelOptions } from './textLabelOptions';

const NAME_LINKS = ['names', 'labels'];
const ANNOTATION_LINKS = ['names', 'labels', 'describes'];
const LOCAL_NAME_TYPES = new Set(['nozzle']);   // names repeat per equipment
const NOT_SYMBOLS = new Set(['text_label', 'pipe_junction', 'drawing_note']);
const LOOKALIKE = { O: '0', Q: '0', I: '1', L: '1', S: '5', B: '8', Z: '2', G: '6' };

const idOf = (v) => (v && typeof v === 'object' ? String(v._id) : v != null ? String(v) : null);

export const normalizeTag = (s) => (s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
export const foldTag = (s) => normalizeTag(s).replace(/[OQILSBZG]/g, ch => LOOKALIKE[ch]);

// Folded strings equal, or (for tags of 5+ characters) one character missing —
// OCR dropping a glyph ("AB03" for AB039). Never a different character in the
// same place: look-alike misreads are already folded, and AB005 vs AB006 is
// another valve.
export function sameTag(a, b) {
  if (a === b) return true;
  if (Math.max(a.length, b.length) < 5 || Math.min(a.length, b.length) < 4 || Math.abs(a.length - b.length) !== 1) return false;
  const [short, long] = a.length < b.length ? [a, b] : [b, a];
  for (let i = 0; i < long.length; i++) {
    if (long.slice(0, i) + long.slice(i + 1) === short) return true;
  }
  return false;
}

// Close matches only for tags long enough that a few shared characters mean
// something: "AB005" vs "AB006" is 0.8 alike but a different valve.
export const CLOSE_MATCH_MIN_LENGTH = 6;
export const CLOSE_MATCH_THRESHOLD = 0.75;

// 2·LCS / (|a| + |b|): 1 for identical strings, 0 for nothing in common.
export function tagSimilarity(a, b) {
  if (!a || !b) return 0;
  let prev = new Array(b.length + 1).fill(0);
  for (let i = 1; i <= a.length; i++) {
    const row = [0];
    for (let j = 1; j <= b.length; j++) {
      row[j] = a[i - 1] === b[j - 1] ? prev[j - 1] + 1 : Math.max(prev[j], row[j - 1]);
    }
    prev = row;
  }
  return (2 * prev[b.length]) / (a.length + b.length);
}

export const REPEATED_TEXT_MIN = 3;

// Folded reading → how many (non-rejected) text labels on the drawing say it.
export function textCounts(elements) {
  const counts = new Map();
  for (const el of elements) {
    if (el.elementType !== 'text_label' || el.metadata?.rejected) continue;
    const k = foldTag(el.name);
    if (k) counts.set(k, (counts.get(k) || 0) + 1);
  }
  return counts;
}

export const vlmTagOf = (element) => element?.metadata?.vlm?.tag_text || element?.metadata?.claude_tag || '';

// The text label already linked as this element's name (names / legacy labels), if any.
export function linkedNameLabel(element, elements, connections) {
  if (!element) return null;
  const link = connections.find(c => (
    NAME_LINKS.includes(c.relationshipType) && !c.metadata?.rejected && idOf(c.target) === String(element._id)
  ));
  return link ? elements.find(el => String(el._id) === idOf(link.source)) || null : null;
}

// { label, reading, tag, close } for the best nearby unlinked label matching
// the element's VLM tag, or null. `reading` is the label's own text — what the
// name becomes; `close` marks a close (not exact) match, shown as such.
export function suggestNameLabel(element, elements, connections) {
  const tag = vlmTagOf(element);
  if (!element || normalizeTag(tag).length < 2) return null;
  if (linkedNameLabel(element, elements, connections)) return null;   // already named by a label

  const whole = foldTag(tag);
  const parts = tag.split(/[\s/]+/).filter(p => normalizeTag(p).length >= 2).map(foldTag);
  // A label that already names something else is left alone.
  const naming = new Set(connections
    .filter(c => NAME_LINKS.includes(c.relationshipType) && !c.metadata?.rejected)
    .map(c => idOf(c.source)));

  const counts = LOCAL_NAME_TYPES.has(element.elementType) ? null : textCounts(elements);
  const candidates = rankTextLabelOptions({ elements, connections, target: element, targetType: 'Element' })
    .filter(o => o.nearby && !naming.has(String(o.label._id)))
    .filter(o => !counts || (counts.get(foldTag(o.label.name)) || 0) < REPEATED_TEXT_MIN)
    .map(o => {
      const text = foldTag(o.label.name);
      if (text.length < 2) return null;
      if (sameTag(text, whole)) return { ...o, rank: 0, score: 1, length: text.length };
      if (parts.some(p => sameTag(text, p))) return { ...o, rank: 1, score: 1, length: text.length };
      const score = Math.min(text.length, whole.length) >= CLOSE_MATCH_MIN_LENGTH ? tagSimilarity(text, whole) : 0;
      return score >= CLOSE_MATCH_THRESHOLD ? { ...o, rank: 2, score, length: text.length } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.rank - b.rank || b.score - a.score || b.length - a.length || a.distance - b.distance);

  if (!candidates.length) return null;
  const best = candidates[0];
  return { label: best.label, reading: best.label.name, tag, close: best.rank === 2 };
}

// Distance between two boxes' edges (0 when they overlap) — so a label beside a
// big vessel counts as close to it, not to its far-away centre.
function boxGap(a, b) {
  const dx = Math.max(0, Math.max(a.x, b.x) - Math.min(a.x + a.width, b.x + b.width));
  const dy = Math.max(0, Math.max(a.y, b.y) - Math.min(a.y + a.height, b.y + b.height));
  return Math.hypot(dx, dy);
}

// Nearby labels that look like this element's description: text repeated
// across the drawing (a spec code like "GV-228", "N.O."), not yet linked to
// anything, and sitting closer to this element than to any other symbol — so a
// neighbouring valve's spec isn't offered. `excludeIds`: labels already in
// use (e.g. the one suggested or accepted as the name). Nearest first.
export function suggestDescriptionLabels(element, elements, connections, { excludeIds = [], max = 3 } = {}) {
  if (!element?.boundingBox || NOT_SYMBOLS.has(element.elementType)) return [];
  const exclude = new Set(excludeIds.map(String));
  const used = new Set(connections
    .filter(c => ANNOTATION_LINKS.includes(c.relationshipType) && !c.metadata?.rejected)
    .map(c => idOf(c.source)));
  const symbols = elements.filter(el => el.boundingBox && !NOT_SYMBOLS.has(el.elementType) && !el.metadata?.rejected);
  const counts = textCounts(elements);
  return rankTextLabelOptions({ elements, connections, target: element, targetType: 'Element' })
    .filter(o => o.nearby && !used.has(String(o.label._id)) && !exclude.has(String(o.label._id)))
    .filter(o => (counts.get(foldTag(o.label.name)) || 0) >= REPEATED_TEXT_MIN)
    .filter(o => {
      const own = boxGap(o.label.boundingBox, element.boundingBox);
      return symbols.every(s => String(s._id) === String(element._id) || boxGap(o.label.boundingBox, s.boundingBox) >= own);
    })
    .slice(0, max)
    .map(o => o.label);
}
